/**
 * Trades Poster — Pricing Plan (Source Register)
 * Permanent Basic and Pro Pricing Plan source records.
 */

import { pricingPlans } from './pricingPlans';

export const BILLING_CYCLE = 'Monthly';
export const GST_TREATMENT = 'Included';
export const CURRENCY = 'AUD';

export const GST_OPTIONS = ['Included', 'Not Included'];
export const BILLING_CYCLE_OPTIONS = [BILLING_CYCLE];

export const PERMANENT_PLAN_ORDER = ['PLAN-001', 'PLAN-002'];

export const PLAN_NAME_OPTIONS = ['Basic', 'Pro'];

export const PLAN_NAME_BY_ID = {
  'PLAN-001': 'Basic',
  'PLAN-002': 'Pro',
};

export const PLAN_ID_BY_NAME = {
  Basic: 'PLAN-001',
  Pro: 'PLAN-002',
};

export const PLAN_KEY_BY_ID = {
  'PLAN-001': 'basic',
  'PLAN-002': 'pro',
};

export const PRICE_HISTORY_STATUS = {
  SCHEDULED: 'Scheduled',
  APPLIED: 'Applied',
  CANCELLED: 'Cancelled',
};

export const STATUS_FILTER_OPTIONS = ['Active', 'Inactive'];

export const PUBLIC_DESCRIPTION_MAX_LENGTH = 200;

export const PRICE_CHANGE_NOTICE_DAYS = 30;

export const SAVE_SUCCESS_MESSAGE = 'Pricing Plan saved and confirmed successfully.';
export const SAVE_FAILURE_MESSAGE =
  'Pricing Plan could not be saved. Review the highlighted fields and try again.';

export const SAVE_CONFIRM_TITLE = 'Save & Confirm this Pricing Plan?';
export const SAVE_CONFIRM_LABEL = 'Save & Confirm';

export const DISCARD_CONFIRM_TITLE = 'Discard unsaved changes?';
export const DISCARD_CONFIRM_LABEL = 'Discard Changes';
export const DISCARD_CONTINUE_LABEL = 'Continue Editing';

export const CANCEL_PENDING_TITLE = 'Cancel this pending price change?';
export const CANCEL_PENDING_CONFIRM_LABEL = 'Cancel Price Change';
export const CANCEL_PENDING_KEEP_LABEL = 'Keep Price Change';

const SAMPLE_ACTOR = 'Saim Sahi';

const SAMPLE_PUBLIC_DESCRIPTIONS = {
  Basic:
    'For qualified tradespeople who want a verified profile, showcase their work, and connect with local customers.',
  Pro:
    'For growing businesses that want stronger reach, more posts, and full access to customer insights and contact options.',
};

function getCurrentMonthlyCost(plan) {
  if (plan == null) return null;
  if (plan.currentMonthlyCost != null && plan.currentMonthlyCost !== '') {
    return Number(plan.currentMonthlyCost);
  }
  if (plan.monthlyCost != null && plan.monthlyCost !== '') {
    return Number(plan.monthlyCost);
  }
  return null;
}

export function resolveCurrentMonthlyCost(plan) {
  return getCurrentMonthlyCost(plan);
}

export function formatGstDisplay(gstIncluded = true) {
  return gstIncluded !== false ? GST_TREATMENT : 'Not Included';
}

export function formatMonthlyCostDisplay(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  return `$${value.toFixed(2)} AUD`;
}

export function formatMonthlyCostFull(amount) {
  return formatMonthlyCostDisplay(amount);
}

export function formatEffectiveFromDisplay(value) {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return value;
  }
}

export function formatAuditDisplay(value) {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return value;
  }
}

export function canAddPricingPlan(plans) {
  const names = new Set((plans || []).map((plan) => plan.planName));
  return !names.has('Basic') || !names.has('Pro');
}

export function getAvailablePlanNames(existingPlans, excludePlanId) {
  const used = new Set(
    (existingPlans || [])
      .filter((plan) => plan.id !== excludePlanId)
      .map((plan) => plan.planName),
  );
  return PLAN_NAME_OPTIONS.filter((name) => !used.has(name));
}

export function generatePlanId(planName) {
  return PLAN_ID_BY_NAME[planName] || '';
}

export function hasPendingPriceChange(plan) {
  const pending = plan?.pendingMonthlyCost;
  return pending != null && pending !== '' && Number.isFinite(Number(pending));
}

export function matchesPricingPlanSearch(plan, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  const current = resolveCurrentMonthlyCost(plan);
  const pending = hasPendingPriceChange(plan) ? Number(plan.pendingMonthlyCost) : null;
  const haystack = [
    plan.id,
    plan.planName,
    current != null ? String(current) : '',
    current != null ? formatMonthlyCostDisplay(current) : '',
    pending != null ? String(pending) : '',
    pending != null ? formatMonthlyCostDisplay(pending) : '',
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function emptyPricingPlanForm() {
  return {
    id: '',
    planName: '',
    currentMonthlyCost: '',
    initialMonthlyCost: '',
    pendingMonthlyCost: '',
    effectiveFrom: '',
    gstIncluded: true,
    billingCycle: BILLING_CYCLE,
    publicDescription: '',
    priceHistory: [],
    addedOn: '',
    addedBy: '',
    lastUpdatedOn: '',
    lastUpdatedBy: '',
    /** UI-only: whether Schedule Price Change fields are expanded in Edit Mode */
    isSchedulingPriceChange: false,
  };
}

function buildInitialHistoryEntry(planId, amount, addedOn) {
  return {
    id: `${planId}-PH-001`,
    previousPrice: null,
    newPrice: Number(amount),
    effectiveFrom: addedOn.slice(0, 10),
    status: PRICE_HISTORY_STATUS.APPLIED,
    changedOn: addedOn,
    changedBy: SAMPLE_ACTOR,
  };
}

function buildPlanRecord(planId) {
  const planKey = PLAN_KEY_BY_ID[planId];
  const planName = PLAN_NAME_BY_ID[planId];
  const publicPlan = pricingPlans[planKey];
  const currentMonthlyCost = Number(publicPlan?.price || 0);
  const addedOn = planId === 'PLAN-001' ? '2026-07-06T10:15:00+10:00' : '2026-07-06T10:25:00+10:00';
  const isPro = planId === 'PLAN-002';

  const priceHistory = isPro
    ? [
        {
          id: 'PLAN-002-PH-003',
          previousPrice: 19.99,
          newPrice: 21.99,
          effectiveFrom: '2026-09-15',
          status: PRICE_HISTORY_STATUS.SCHEDULED,
          changedOn: '2026-08-04T09:30:00+10:00',
          changedBy: SAMPLE_ACTOR,
        },
        {
          id: 'PLAN-002-PH-002',
          previousPrice: 17.99,
          newPrice: 19.99,
          effectiveFrom: '2026-06-01',
          status: PRICE_HISTORY_STATUS.APPLIED,
          changedOn: '2026-04-30T11:20:00+10:00',
          changedBy: SAMPLE_ACTOR,
        },
        buildInitialHistoryEntry(planId, 17.99, '2026-03-01T09:00:00+10:00'),
      ]
    : [buildInitialHistoryEntry(planId, currentMonthlyCost, addedOn)];

  return {
    id: planId,
    planName,
    currentMonthlyCost,
    pendingMonthlyCost: isPro ? 21.99 : null,
    effectiveFrom: isPro ? '2026-09-15' : null,
    gstIncluded: true,
    billingCycle: BILLING_CYCLE,
    publicDescription: SAMPLE_PUBLIC_DESCRIPTIONS[planName] || publicPlan?.description || '',
    status: 'Active',
    priceHistory,
    addedOn,
    addedBy: SAMPLE_ACTOR,
    lastUpdatedOn: isPro ? '2026-08-04T09:30:00+10:00' : addedOn,
    lastUpdatedBy: SAMPLE_ACTOR,
  };
}

export function buildSamplePricingPlanManagementRecords() {
  return PERMANENT_PLAN_ORDER.map(buildPlanRecord);
}
